import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, SectionHead, Eyebrow } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { PARTNER_AUDIENCES } from "@/data/site";

export default function Partners() {
  return (
    <>
      <Seo
        title="Partners — BNM Sustainable Energy Limited"
        description="Partner with BNM: investors, energy companies, NGOs, development organisations, government and research institutions building clean energy in Africa."
      />
      <PageHero
        testid="partners-hero"
        eyebrow="Partnerships"
        lines={["Let's build the future", "of energy together."]}
        lead="BNM is developing technology with continental ambition. We are looking for partners who want to build — not just observe — Africa's clean-energy transition."
      />

      <section data-testid="partners-audiences" className="bg-mist">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            eyebrow="Who We Partner With"
            title="Six communities, one energy future."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {PARTNER_AUDIENCES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.06}>
                <article className="h-full rounded-lg border border-slate-200 bg-white p-7 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-300">
                  <p className="font-mono text-xs text-emerald-600">0{i + 1}</p>
                  <h3 className="mt-4 font-display text-lg font-bold tracking-tight text-slate-900">{a.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">{a.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="partners-why" className="relative overflow-hidden bg-obsidian noise-overlay">
        <div className="absolute inset-0 hero-glow pointer-events-none" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <SectionHead
            dark
            eyebrow="Why Partner With BNM"
            title="Early partners shape the platform."
            lead="BNM is at the prototype stage — the moment when partners have the greatest influence over product direction, pilot design and market strategy."
          />
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6">
            {[
              { t: "Ground-Floor Access", d: "Engage with the Carbon Free Generator programme while architecture decisions are still open." },
              { t: "African Market Entry", d: "Build clean-energy presence in Sierra Leone and West Africa with a local technology partner." },
              { t: "Verified Milestones", d: "BNM reports progress conservatively and shares validation data with committed partners." },
            ].map((c, i) => (
              <Reveal key={c.t} delay={i * 0.08}>
                <article className="h-full rounded-lg border border-white/10 bg-pine/60 p-7">
                  <h3 className="font-display text-lg font-bold tracking-tight text-white">{c.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-400">{c.d}</p>
                </article>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-14 border-t border-white/10 pt-10">
              <Eyebrow dark>Begin the Conversation</Eyebrow>
              <p className="mt-4 max-w-2xl font-display text-2xl sm:text-3xl font-bold tracking-tight text-white text-balance">
                Let's Build the Future of Energy Together.
              </p>
              <Link
                to="/contact"
                data-testid="partners-contact-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-md bg-volt px-8 py-4 font-semibold text-obsidian transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-volt-soft hover:text-obsidian active:translate-y-0"
              >
                Contact BNM Partnerships
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
